/** ScheduledTaskDetailDrawer — 概览 + 执行历史 tabs for one scheduled task.
 *
 * Self-contained like MeetingDetailDrawer: owns its own pause/resume/run-now/
 * delete mutations and toast context rather than reaching back into the list
 * for them, so it works whether opened from a row click or (eventually) a
 * deep link. Workflow-type runs with a linked execution jump to
 * `/pipelines/:executionId` (contract §8.3, "工作流类可跳转对应执行详情"). */

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Drawer,
  Empty,
  List,
  Pagination,
  Popconfirm,
  Space,
  Tabs,
  Tag,
  Tooltip,
  Typography,
  message,
} from "antd";
import {
  CaretRightOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  PauseCircleOutlined,
  RocketOutlined,
} from "@ant-design/icons";
import {
  useDeleteScheduledTask,
  usePauseScheduledTask,
  useResumeScheduledTask,
  useRunScheduledTaskNow,
  useScheduledTask,
  useScheduledTaskRuns,
} from "../../api/queries";
import {
  SCHEDULED_TASK_RUN_STATUS_LABELS,
  SCHEDULED_TASK_STATUS_LABELS,
  SCHEDULED_TASK_TARGET_TYPE_LABELS,
  SCHEDULED_TASK_TRIGGER_KIND_LABELS,
  type ScheduledTask,
} from "../../types/scheduledTask";
import { AGENT_RUN_STATUS_LABELS, AGENT_RUN_STATUS_TAG_COLORS } from "../../types/agentRun";
import { formatDateTime } from "../../utils/date";
import EmptyState from "../common/EmptyState";
import ScheduledRunReviewDrawer from "./ScheduledRunReviewDrawer";
import {
  RUN_STATUS_TAG_COLOR,
  STATUS_TAG_COLOR,
  TARGET_TYPE_ICON,
  TARGET_TYPE_TAG_COLOR,
  describeTaskActionError,
} from "./constants";

const { Text, Paragraph } = Typography;
const RUNS_PAGE_SIZE = 20;

interface ScheduledTaskDetailDrawerProps {
  taskId: string | null;
  open: boolean;
  onClose: () => void;
  onEdit: (task: ScheduledTask) => void;
  /**
   * 期产物视图锚点（波次 2 深链 ?run=）。受控可选：传入时以外部为准
   * （页面深链在用），缺省时内部自管（run 行「查看期产物」点击）。
   */
  reviewRunId?: string | null;
  onReviewRunChange?: (runId: string | null) => void;
}

export default function ScheduledTaskDetailDrawer({
  taskId,
  open,
  onClose,
  onEdit,
  reviewRunId: reviewRunIdProp,
  onReviewRunChange,
}: ScheduledTaskDetailDrawerProps) {
  const navigate = useNavigate();
  const [messageApi, contextHolder] = message.useMessage();
  const [runsPage, setRunsPage] = useState(1);
  const [internalReviewRunId, setInternalReviewRunId] = useState<string | null>(null);
  const reviewRunId = reviewRunIdProp ?? internalReviewRunId;
  const setReviewRunId = onReviewRunChange ?? setInternalReviewRunId;

  const {
    data: task,
    isLoading,
    isError: isTaskError,
    refetch: refetchTask,
  } = useScheduledTask(open ? taskId : null);
  const runsQuery = useScheduledTaskRuns(open ? taskId : null, runsPage, RUNS_PAGE_SIZE);
  const runs = runsQuery.data?.data ?? [];
  const runsTotal = runsQuery.data?.meta?.total ?? 0;

  const pauseMut = usePauseScheduledTask();
  const resumeMut = useResumeScheduledTask();
  const runNowMut = useRunScheduledTaskNow();
  const deleteMut = useDeleteScheduledTask();

  const handlePause = async () => {
    if (!task) return;
    try {
      await pauseMut.mutateAsync(task.id);
      messageApi.success("已暂停");
    } catch (err) {
      messageApi.error(describeTaskActionError(err, "任务状态已变化，请刷新后重试"));
    }
  };

  const handleResume = async () => {
    if (!task) return;
    try {
      await resumeMut.mutateAsync(task.id);
      messageApi.success("已恢复");
    } catch (err) {
      messageApi.error(describeTaskActionError(err, "任务状态已变化，请刷新后重试"));
    }
  };

  const handleRunNow = async () => {
    if (!task) return;
    try {
      await runNowMut.mutateAsync(task.id);
      messageApi.success("已触发本次执行");
    } catch (err) {
      messageApi.error(describeTaskActionError(err, "该工作流实例存在正在执行的任务，请稍后重试"));
    }
  };

  const handleDelete = async () => {
    if (!task) return;
    try {
      await deleteMut.mutateAsync(task.id);
      messageApi.success("已删除");
      onClose();
    } catch (err) {
      messageApi.error(describeTaskActionError(err, "删除失败，请刷新后重试"));
    }
  };

  return (
    <Drawer
      title={task?.name ?? "定时任务详情"}
      open={open}
      onClose={onClose}
      width={640}
      loading={isLoading}
      destroyOnHidden
      data-testid="scheduled-task-detail-drawer"
      extra={
        task && (
          <Space>
            {/* once 任务与列表行操作同口径：仅 查看执行 + 删除（立即执行/
                编辑对单次任务后端一律拒绝——触发后不重臂、定义不可再动）。 */}
            {task.frequency === "once" ? (
              <>
                <Tooltip title="查看执行">
                  <Button
                    icon={<EyeOutlined />}
                    disabled={!task.lastRun?.agentRunId}
                    onClick={() => task.lastRun?.agentRunId && navigate(`/agent-runs/${task.lastRun.agentRunId}`)}
                  />
                </Tooltip>
                <Popconfirm
                  title="确认删除该任务？"
                  description="删除后该任务将从列表中移除，已产生的执行历史仍保留以供审计。"
                  okText="删除"
                  cancelText="取消"
                  okButtonProps={{ danger: true }}
                  onConfirm={handleDelete}
                >
                  <Button danger icon={<DeleteOutlined />}>
                    删除
                  </Button>
                </Popconfirm>
              </>
            ) : (
              <>
                {task.status === "active" && (
                  <Tooltip title="暂停">
                    <Button icon={<PauseCircleOutlined />} loading={pauseMut.isPending} onClick={handlePause} />
                  </Tooltip>
                )}
                {task.status === "paused" && (
                  <Tooltip title="恢复">
                    <Button icon={<CaretRightOutlined />} loading={resumeMut.isPending} onClick={handleResume} />
                  </Tooltip>
                )}
                <Tooltip title="立即执行">
                  <Button
                    icon={<RocketOutlined />}
                    disabled={task.status === "disabled"}
                    loading={runNowMut.isPending}
                    onClick={handleRunNow}
                  />
                </Tooltip>
                <Button icon={<EditOutlined />} onClick={() => onEdit(task)}>
                  编辑
                </Button>
                <Popconfirm
                  title="确认删除该定时任务？"
                  description="删除后该任务将停止调度并从列表中移除，已产生的执行历史仍保留以供审计。"
                  okText="删除"
                  cancelText="取消"
                  okButtonProps={{ danger: true }}
                  onConfirm={handleDelete}
                >
                  <Button danger icon={<DeleteOutlined />}>
                    删除
                  </Button>
                </Popconfirm>
              </>
            )}
          </Space>
        )
      }
    >
      {contextHolder}
      {!isLoading && isTaskError && (
        <EmptyState
          title="加载失败"
          description="无法获取该定时任务的详情，请稍后重试。"
          action={
            <Button type="primary" onClick={() => refetchTask()}>
              重试
            </Button>
          }
        />
      )}
      {task && (
        <Tabs
          defaultActiveKey="overview"
          items={[
            {
              key: "overview",
              label: "概览",
              children: (
                <div>
                  <Paragraph>
                    <Space size="small" wrap>
                      <Tag icon={TARGET_TYPE_ICON[task.targetType]} color={TARGET_TYPE_TAG_COLOR[task.targetType]}>
                        {SCHEDULED_TASK_TARGET_TYPE_LABELS[task.targetType]}
                      </Tag>
                      <Tag color={STATUS_TAG_COLOR[task.status]}>{SCHEDULED_TASK_STATUS_LABELS[task.status]}</Tag>
                    </Space>
                  </Paragraph>
                  {task.status === "disabled" && task.disabledReason && (
                    <Paragraph type="danger">停用原因：{task.disabledReason}</Paragraph>
                  )}
                  {task.description && <Paragraph>{task.description}</Paragraph>}

                  <Paragraph>
                    <Text strong>执行目标：</Text>
                    {task.targetType === "pipeline_instance"
                      ? (task.instanceName ?? "工作流实例已删除")
                      : task.targetType === "pipeline_template"
                        ? `模板 ${task.templateName ?? task.templateId ?? "-"} · ${task.matterName ?? "-"}`
                        : `${task.matterName ?? "-"} · ${task.reminderTitle ?? "-"}`}
                  </Paragraph>
                  {task.targetType === "matter_reminder" && task.reminderNote && (
                    <Paragraph type="secondary">{task.reminderNote}</Paragraph>
                  )}

                  {/* AI 任务（agent_run 目标）：任务目标 + 预算三元组。目标为空
                      表示每期按模板内置目标渲染（once 任务由创建口径恒有目标）。 */}
                  {task.targetType === "agent_run" && (
                    <>
                      <Paragraph>
                        <Text strong>任务目标：</Text>
                        {task.goal?.trim() || (
                          <Text type="secondary">按模板内置目标执行</Text>
                        )}
                      </Paragraph>
                      <Paragraph>
                        <Text strong>预算上限：</Text>
                        步数 {task.maxSteps ?? "不限"} · 时长{" "}
                        {task.maxWallClockMinutes ?? "不限"} 分钟 · token{" "}
                        {task.maxTotalTokens ?? "不限"}
                      </Paragraph>
                    </>
                  )}

                  <Paragraph>
                    <Text strong>执行频率：</Text>
                    {task.frequencyText}
                  </Paragraph>
                  <Paragraph>
                    <Text strong>下次执行：</Text>
                    {task.status === "active" && task.nextRunAt ? formatDateTime(task.nextRunAt) : "-"}
                  </Paragraph>
                  <Paragraph>
                    <Text strong>上次触发：</Text>
                    {task.lastTriggeredAt ? formatDateTime(task.lastTriggeredAt) : "从未触发"}
                  </Paragraph>
                  <Paragraph>
                    <Text strong>已执行次数：</Text>
                    {task.runCount}
                    {task.maxRuns != null && ` / ${task.maxRuns}`}
                  </Paragraph>
                </div>
              ),
            },
            {
              key: "runs",
              label: "执行历史",
              children: runsQuery.isError ? (
                <EmptyState
                  title="加载失败"
                  description="无法获取执行历史，请稍后重试。"
                  action={
                    <Button type="primary" onClick={() => runsQuery.refetch()}>
                      重试
                    </Button>
                  }
                />
              ) : (
                <div>
                  <List
                    loading={runsQuery.isLoading}
                    dataSource={runs}
                    locale={{
                      emptyText: (
                        <Empty description="任务触发后，每次执行结果会显示在这里" />
                      ),
                    }}
                    renderItem={(run) => (
                      <List.Item
                        key={run.id}
                        actions={
                          // AI 任务（agent_run）：执行状态以关联 agent run 为准，
                          // 每期可跳执行视图（替代仅工作流类有链接的不对称）。
                          task.targetType === "agent_run" && run.agentRunId
                            ? [
                                <Button
                                  key="view-agent-run"
                                  type="link"
                                  size="small"
                                  onClick={() => navigate(`/agent-runs/${run.agentRunId}`)}
                                  data-testid={`scheduled-run-view-agent-run-${run.id}`}
                                >
                                  查看执行详情
                                </Button>,
                              ]
                            : // 波次 2：模板级目标的执行也可跳转（每期实例化出独立执行）。
                              run.executionId &&
                                (task.targetType === "pipeline_instance" ||
                                  task.targetType === "pipeline_template")
                              ? [
                                  <Button
                                    key="view"
                                    type="link"
                                    size="small"
                                    onClick={() => navigate(`/pipelines/${run.executionId}`)}
                                  >
                                    查看执行详情
                                  </Button>,
                                  ...(task.targetType === "pipeline_template"
                                    ? [
                                        <Button
                                          key="review"
                                          type="link"
                                          size="small"
                                          onClick={() => setReviewRunId(run.id)}
                                          data-testid={`scheduled-run-review-open-${run.id}`}
                                        >
                                          查看期产物
                                        </Button>,
                                      ]
                                    : []),
                                ]
                              : task.targetType === "pipeline_template"
                                ? [
                                    <Button
                                      key="review"
                                      type="link"
                                      size="small"
                                      onClick={() => setReviewRunId(run.id)}
                                      data-testid={`scheduled-run-review-open-${run.id}`}
                                    >
                                      查看期产物
                                    </Button>,
                                  ]
                                : undefined
                        }
                      >
                        <Space orientation="vertical" size={4} style={{ width: "100%" }}>
                          <Space wrap>
                            {/* agent_run 目标：audit 行只代表「派发完成」，执行
                                状态以关联 agent run 六态为准。 */}
                            {task.targetType === "agent_run" && run.agentRunStatus ? (
                              <Tooltip title={run.agentRunSummary ?? undefined}>
                                <Tag
                                  color={AGENT_RUN_STATUS_TAG_COLORS[run.agentRunStatus] ?? "default"}
                                  data-testid={`run-agent-run-status-${run.id}`}
                                >
                                  {AGENT_RUN_STATUS_LABELS[run.agentRunStatus] ?? run.agentRunStatus}
                                </Tag>
                              </Tooltip>
                            ) : (
                              <Tag color={RUN_STATUS_TAG_COLOR[run.status]}>
                                {SCHEDULED_TASK_RUN_STATUS_LABELS[run.status]}
                              </Tag>
                            )}
                            <Tag>{SCHEDULED_TASK_TRIGGER_KIND_LABELS[run.triggerKind]}</Tag>
                            <Text type="secondary">计划时间 {formatDateTime(run.scheduledAt)}</Text>
                            {run.triggeredAt && (
                              <Text type="secondary">实际触发 {formatDateTime(run.triggeredAt)}</Text>
                            )}
                          </Space>
                          {run.agentRunSummary && (
                            <Text type="secondary">{run.agentRunSummary}</Text>
                          )}
                          {run.resolvedInputs && (
                            <Tooltip
                              title={
                                run.resolvedInputs.truncated
                                  ? "本期命中文档超过上限，超出部分未纳入执行"
                                  : "本期命中并纳入执行的文档范围"
                              }
                            >
                              <Tag
                                color={run.resolvedInputs.truncated ? "warning" : "default"}
                                data-testid={`run-resolved-inputs-${run.id}`}
                              >
                                命中 {run.resolvedInputs.totalHit} 份，取最近{" "}
                                {run.resolvedInputs.documentIds.length} 份
                              </Tag>
                            </Tooltip>
                          )}
                          {run.status === "failed" && run.errorMessage && (
                            <Text type="danger">失败原因：{run.errorMessage}</Text>
                          )}
                          {run.status === "skipped" && run.skippedReason && (
                            <Text type="secondary">跳过原因：{run.skippedReason}</Text>
                          )}
                        </Space>
                      </List.Item>
                    )}
                  />
                  {runsTotal > RUNS_PAGE_SIZE && (
                    <div style={{ marginTop: "var(--jerry-sp-3)", textAlign: "right" }}>
                      <Pagination
                        size="small"
                        current={runsPage}
                        pageSize={RUNS_PAGE_SIZE}
                        total={runsTotal}
                        onChange={setRunsPage}
                        showSizeChanger={false}
                      />
                    </div>
                  )}
                </div>
              ),
            },
          ]}
        />
      )}
      {task && (
        <ScheduledRunReviewDrawer
          taskId={task.id}
          runId={reviewRunId}
          open={!!reviewRunId}
          onClose={() => setReviewRunId(null)}
        />
      )}
    </Drawer>
  );
}
